import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useListCarrierAddOnServices,
  useUpdateCarrierAddOnService,
  type UpdateCarrierAddOnServiceInput,
  type SpecialHandlingServiceInput,
} from "@/api/carrier-add-on-services";
import { useGetCarriers } from "@/api/carriers";
import type { CarrierAddOnService } from "@/api.types.v2";
import { ServiceEnablementPanel } from "./service-enablement-panel";

/**
 * Tabbed detail view for a single SPECIAL_HANDLING add-on service. Three
 * tabs: Overview (code/name/active/notes), Pricing (flat fee · percentage ·
 * min · max — same shape as INSURANCE), Enablement (reuses
 * ServiceEnablementPanel with type=SPECIAL_HANDLING). Mirrors the
 * InsuranceServiceDetailScreen with notes added to Overview.
 */
export function SpecialHandlingServiceDetailScreen() {
  const { id } = useParams<{ id: string }>();

  const { data: services, isLoading: servicesLoading } =
    useListCarrierAddOnServices({ type: "SPECIAL_HANDLING" });
  const { data: carriersResp } = useGetCarriers();

  const service = id ? services?.find((s) => s.id === id) : undefined;
  const providerName =
    carriersResp?.data.find((c) => c.id === service?.providerCarrierId)?.name ??
    "—";

  if (servicesLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-4 space-y-4">
        <div className="text-center p-8 text-muted-foreground">
          <p>Special handling service not found.</p>
          <Button asChild variant="link" size="sm" className="mt-2">
            <Link to="/add-on-services/special-handling">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Special Handling Services
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <DetailHeader service={service} providerName={providerName} />

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="enablement">Enablement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab service={service} />
        </TabsContent>
        <TabsContent value="pricing">
          <PricingTab service={service} />
        </TabsContent>
        <TabsContent value="enablement">
          <ServiceEnablementPanel
            serviceId={service.id}
            type="SPECIAL_HANDLING"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DetailHeader({
  service,
  providerName,
}: {
  service: CarrierAddOnService;
  providerName: string;
}) {
  return (
    <div className="space-y-2">
      <div className="text-sm">
        <Link
          to="/add-on-services/special-handling"
          className="text-muted-foreground hover:text-foreground inline-flex items-center"
        >
          <ArrowLeft className="h-3 w-3 mr-1" />
          Special Handling Services
        </Link>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold">{service.name}</h1>
          <code className="font-mono text-sm text-muted-foreground">
            {service.code}
          </code>
          {service.active ? (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant="secondary"
              className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100"
            >
              Inactive
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Provider: <span className="text-foreground">{providerName}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers — dollars↔cents conversion + dirty-check parsing. Same approach as
// insurance / packaging detail screens.
// ---------------------------------------------------------------------------

function parseDollarsToCents(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100);
}

function parsePercentage(s: string): number | null | undefined {
  const trimmed = s.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

function centsToDollarString(cents: number | null | undefined): string {
  if (cents == null) return "";
  return String(cents / 100);
}

// ---------------------------------------------------------------------------
// Overview tab — code (RO), name, active, notes (multi-line). Notes is a
// SPECIAL_HANDLING-specific extension field, but lives on Overview to keep
// Pricing focused on numbers.
// ---------------------------------------------------------------------------
function OverviewTab({ service }: { service: CarrierAddOnService }) {
  const initialNotes = service.specialHandlingDetails?.notes ?? null;

  const [name, setName] = useState(service.name);
  const [active, setActive] = useState(service.active);
  const [notes, setNotes] = useState(initialNotes ?? "");

  // Re-seed only on :id change — same pattern as packaging Overview tab.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const s = serviceRef.current;
    setName(s.name);
    setActive(s.active);
    setNotes(s.specialHandlingDetails?.notes ?? "");
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  // `null` → nothing to send, `undefined` → invalid (toast fired).
  const buildPatch = (): UpdateCarrierAddOnServiceInput | null | undefined => {
    const patch: UpdateCarrierAddOnServiceInput = {};
    const trimmedName = name.trim();
    if (trimmedName && trimmedName !== service.name) patch.name = trimmedName;
    if (active !== service.active) patch.active = active;

    // Notes: blank string clears, non-blank trims + sends.
    const trimmedNotes = notes.trim();
    const nextNotes: string | null = trimmedNotes === "" ? null : trimmedNotes;
    if (nextNotes !== initialNotes) {
      patch.specialHandling = { notes: nextNotes };
    }

    return Object.keys(patch).length === 0 ? null : patch;
  };

  const hasChanges = (() => {
    if (name.trim() && name.trim() !== service.name) return true;
    if (active !== service.active) return true;
    const trimmedNotes = notes.trim();
    const nextNotes: string | null = trimmedNotes === "" ? null : trimmedNotes;
    if (nextNotes !== initialNotes) return true;
    return false;
  })();

  const handleSave = () => {
    const patch = buildPatch();
    if (patch === undefined) return;
    if (patch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: patch },
      {
        onSuccess: (updated) => {
          toast.success("Special handling service updated.");
          setName(updated.name);
          setActive(updated.active);
          setNotes(updated.specialHandlingDetails?.notes ?? "");
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update service.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Overview</CardTitle>
        <p className="text-sm text-muted-foreground">
          Code <code className="font-mono">{service.code}</code> and provider
          carrier can&apos;t be changed. To retire, toggle off the Active
          switch.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <Label htmlFor="sh-detail-code">Code</Label>
          <Input
            id="sh-detail-code"
            value={service.code}
            readOnly
            disabled
            className="font-mono"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sh-detail-name">Name</Label>
          <Input
            id="sh-detail-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sh-detail-notes">Notes</Label>
          <Textarea
            id="sh-detail-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Operator-facing context (optional)."
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            Free-form notes — surfaced to ops, not to customers.
          </p>
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <Label className="text-sm font-medium">Active</Label>
            <p className="text-xs text-muted-foreground">
              Inactive services are hidden from booking flows but preserved for
              existing references.
            </p>
          </div>
          <Button
            variant={active ? "outline" : "default"}
            size="sm"
            onClick={() => setActive(!active)}
          >
            {active ? (
              <>
                <X className="h-3 w-3 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reactivate
              </>
            )}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pricing tab — flat fee · percentage · min · max. Same shape as Insurance's
// Pricing tab (no coverage/currency). Mutual exclusion of flat vs percentage
// is NOT enforced — admin discretion. We only send fields that changed.
// ---------------------------------------------------------------------------
function PricingTab({ service }: { service: CarrierAddOnService }) {
  const initialFlat = service.specialHandlingDetails?.flatFeeCents ?? null;
  const initialPct = service.specialHandlingDetails?.percentageRate ?? null;
  const initialMin = service.specialHandlingDetails?.minimumCents ?? null;
  const initialMax = service.specialHandlingDetails?.maximumCents ?? null;

  const [flatFeeDollars, setFlatFeeDollars] = useState(
    centsToDollarString(initialFlat)
  );
  const [percentageRate, setPercentageRate] = useState(
    initialPct != null ? String(initialPct) : ""
  );
  const [minimumDollars, setMinimumDollars] = useState(
    centsToDollarString(initialMin)
  );
  const [maximumDollars, setMaximumDollars] = useState(
    centsToDollarString(initialMax)
  );

  // Re-seed on :id change.
  const serviceRef = useRef(service);
  serviceRef.current = service;
  useEffect(() => {
    const d = serviceRef.current.specialHandlingDetails;
    setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
    setPercentageRate(
      d?.percentageRate != null ? String(d.percentageRate) : ""
    );
    setMinimumDollars(centsToDollarString(d?.minimumCents ?? null));
    setMaximumDollars(centsToDollarString(d?.maximumCents ?? null));
  }, [service.id]);

  const update = useUpdateCarrierAddOnService();

  const buildPatch = (): SpecialHandlingServiceInput | null | undefined => {
    const patch: SpecialHandlingServiceInput = {};

    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) {
      toast.error("Flat fee must be a non-negative number or blank.");
      return undefined;
    }
    if (flat !== initialFlat) patch.flatFeeCents = flat;

    const pct = parsePercentage(percentageRate);
    if (pct === undefined) {
      toast.error("Percentage rate must be a non-negative number or blank.");
      return undefined;
    }
    if (pct !== initialPct) patch.percentageRate = pct;

    const min = parseDollarsToCents(minimumDollars);
    if (min === undefined) {
      toast.error("Minimum must be a non-negative number or blank.");
      return undefined;
    }
    if (min !== initialMin) patch.minimumCents = min;

    const max = parseDollarsToCents(maximumDollars);
    if (max === undefined) {
      toast.error("Maximum must be a non-negative number or blank.");
      return undefined;
    }
    if (max !== initialMax) patch.maximumCents = max;

    return Object.keys(patch).length === 0 ? null : patch;
  };

  const hasChanges = (() => {
    const flat = parseDollarsToCents(flatFeeDollars);
    if (flat === undefined) return true;
    if (flat !== initialFlat) return true;
    const pct = parsePercentage(percentageRate);
    if (pct === undefined) return true;
    if (pct !== initialPct) return true;
    const min = parseDollarsToCents(minimumDollars);
    if (min === undefined) return true;
    if (min !== initialMin) return true;
    const max = parseDollarsToCents(maximumDollars);
    if (max === undefined) return true;
    if (max !== initialMax) return true;
    return false;
  })();

  const handleSave = () => {
    const specialHandlingPatch = buildPatch();
    if (specialHandlingPatch === undefined) return;
    if (specialHandlingPatch === null) {
      toast.message("Nothing to update.");
      return;
    }
    update.mutate(
      { id: service.id, data: { specialHandling: specialHandlingPatch } },
      {
        onSuccess: (updated) => {
          toast.success("Pricing updated.");
          const d = updated.specialHandlingDetails;
          setFlatFeeDollars(centsToDollarString(d?.flatFeeCents ?? null));
          setPercentageRate(
            d?.percentageRate != null ? String(d.percentageRate) : ""
          );
          setMinimumDollars(centsToDollarString(d?.minimumCents ?? null));
          setMaximumDollars(centsToDollarString(d?.maximumCents ?? null));
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to update pricing.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pricing</CardTitle>
        <p className="text-sm text-muted-foreground">
          Flat fee OR percentage-with-clamps. Mutual exclusion is not enforced —
          admin-discretion which pattern to use.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="shp-flat">Flat fee (CAD)</Label>
            <Input
              id="shp-flat"
              type="number"
              step="any"
              value={flatFeeDollars}
              onChange={(e) => setFlatFeeDollars(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              A fixed fee charged when this handling is requested.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="shp-pct">Percentage rate (%)</Label>
            <Input
              id="shp-pct"
              type="number"
              step="any"
              value={percentageRate}
              onChange={(e) => setPercentageRate(e.target.value)}
              placeholder="—"
            />
            <p className="text-xs text-muted-foreground">
              e.g. <code>2.5</code> means 2.5% of declared value.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="shp-min">Minimum (CAD)</Label>
            <Input
              id="shp-min"
              type="number"
              step="any"
              value={minimumDollars}
              onChange={(e) => setMinimumDollars(e.target.value)}
              placeholder="—"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="shp-max">Maximum (CAD)</Label>
            <Input
              id="shp-max"
              type="number"
              step="any"
              value={maximumDollars}
              onChange={(e) => setMaximumDollars(e.target.value)}
              placeholder="—"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={update.isPending || !hasChanges}
          >
            <Save className="h-4 w-4 mr-1" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
